import type { Chain as ViemChain } from 'viem';
import {
  mainnet,
  base,
  arbitrum,
  optimism,
  polygon,
  bsc,
  avalanche,
  robinhood as robinhoodViem,
  sepolia,
  baseSepolia,
  arbitrumSepolia,
  optimismSepolia,
  polygonAmoy,
  bscTestnet,
  avalancheFuji,
  robinhoodTestnet as robinhoodTestnetViem,
} from 'viem/chains';
import { config, type NetworkMode } from './config';
import type { Chain } from './api/types';

export type ChainKind = 'evm' | 'solana' | 'tron' | 'utxo';
export type Ecosystem = 'bitcoin-like' | 'evm' | 'solana' | 'tron';

export interface ChainMeta {
  id: Chain;
  kind: ChainKind;
  ecosystem: Ecosystem;
  /** Native asset ticker. */
  native: string;
  label: string;
  /** Brand-ish colour for the chain dot. */
  color: string;
  /** Public RPC (EVM / Solana / Tron) per network. UTXO chains are watched server-side only. */
  rpc: Record<NetworkMode, string>;
  /** Explorer base URL for a tx hash. */
  explorerTx: Record<NetworkMode, string>;
  /** Human label for the testnet. */
  testnetName: string;
}

export const CHAINS: Record<Chain, ChainMeta> = {
  bitcoin: {
    id: 'bitcoin',
    kind: 'utxo',
    ecosystem: 'bitcoin-like',
    native: 'BTC',
    label: 'Bitcoin',
    color: '#F7931A',
    rpc: { mainnet: 'https://mempool.space/api', testnet: 'https://mempool.space/testnet/api' },
    explorerTx: { mainnet: 'https://mempool.space/tx/', testnet: 'https://mempool.space/testnet/tx/' },
    testnetName: 'Testnet3',
  },
  ethereum: {
    id: 'ethereum',
    kind: 'evm',
    ecosystem: 'evm',
    native: 'ETH',
    label: 'Ethereum',
    color: '#627EEA',
    rpc: { mainnet: 'https://eth.llamarpc.com', testnet: 'https://rpc.sepolia.org' },
    explorerTx: { mainnet: 'https://etherscan.io/tx/', testnet: 'https://sepolia.etherscan.io/tx/' },
    testnetName: 'Sepolia',
  },
  base: {
    id: 'base',
    kind: 'evm',
    ecosystem: 'evm',
    native: 'ETH',
    label: 'Base',
    color: '#0052FF',
    rpc: { mainnet: 'https://mainnet.base.org', testnet: 'https://sepolia.base.org' },
    explorerTx: { mainnet: 'https://basescan.org/tx/', testnet: 'https://sepolia.basescan.org/tx/' },
    testnetName: 'Sepolia',
  },
  arbitrum: {
    id: 'arbitrum',
    kind: 'evm',
    ecosystem: 'evm',
    native: 'ETH',
    label: 'Arbitrum',
    color: '#28A0F0',
    rpc: { mainnet: 'https://arb1.arbitrum.io/rpc', testnet: 'https://sepolia-rollup.arbitrum.io/rpc' },
    explorerTx: { mainnet: 'https://arbiscan.io/tx/', testnet: 'https://sepolia.arbiscan.io/tx/' },
    testnetName: 'Sepolia',
  },
  optimism: {
    id: 'optimism',
    kind: 'evm',
    ecosystem: 'evm',
    native: 'ETH',
    label: 'Optimism',
    color: '#FF0420',
    rpc: { mainnet: 'https://mainnet.optimism.io', testnet: 'https://sepolia.optimism.io' },
    explorerTx: { mainnet: 'https://optimistic.etherscan.io/tx/', testnet: 'https://sepolia-optimism.etherscan.io/tx/' },
    testnetName: 'Sepolia',
  },
  polygon: {
    id: 'polygon',
    kind: 'evm',
    ecosystem: 'evm',
    native: 'POL',
    label: 'Polygon',
    color: '#8247E5',
    rpc: { mainnet: 'https://polygon-rpc.com', testnet: 'https://rpc-amoy.polygon.technology' },
    explorerTx: { mainnet: 'https://polygonscan.com/tx/', testnet: 'https://amoy.polygonscan.com/tx/' },
    testnetName: 'Amoy',
  },
  bnb: {
    id: 'bnb',
    kind: 'evm',
    ecosystem: 'evm',
    native: 'BNB',
    label: 'BNB Chain',
    color: '#F3BA2F',
    rpc: { mainnet: 'https://bsc-dataseed.bnbchain.org', testnet: 'https://data-seed-prebsc-1-s1.bnbchain.org:8545' },
    explorerTx: { mainnet: 'https://bscscan.com/tx/', testnet: 'https://testnet.bscscan.com/tx/' },
    testnetName: 'Testnet',
  },
  avalanche: {
    id: 'avalanche',
    kind: 'evm',
    ecosystem: 'evm',
    native: 'AVAX',
    label: 'Avalanche',
    color: '#E84142',
    rpc: { mainnet: 'https://api.avax.network/ext/bc/C/rpc', testnet: 'https://api.avax-test.network/ext/bc/C/rpc' },
    explorerTx: { mainnet: 'https://snowtrace.io/tx/', testnet: 'https://testnet.snowtrace.io/tx/' },
    testnetName: 'Fuji',
  },
  robinhood: {
    id: 'robinhood',
    kind: 'evm',
    ecosystem: 'evm',
    native: 'ETH',
    label: 'Robinhood Chain',
    color: '#00C805',
    rpc: { mainnet: 'https://rpc.mainnet.chain.robinhood.com', testnet: 'https://rpc.testnet.chain.robinhood.com' },
    explorerTx: { mainnet: 'https://robinhoodchain.blockscout.com/tx/', testnet: 'https://explorer.testnet.chain.robinhood.com/tx/' },
    testnetName: 'Testnet',
  },
  solana: {
    id: 'solana',
    kind: 'solana',
    ecosystem: 'solana',
    native: 'SOL',
    label: 'Solana',
    color: '#9945FF',
    rpc: { mainnet: 'https://api.mainnet-beta.solana.com', testnet: 'https://api.devnet.solana.com' },
    explorerTx: { mainnet: 'https://solscan.io/tx/', testnet: 'https://solscan.io/tx/' },
    testnetName: 'Devnet',
  },
  tron: {
    id: 'tron',
    kind: 'tron',
    ecosystem: 'tron',
    native: 'TRX',
    label: 'Tron',
    color: '#EF0027',
    rpc: { mainnet: 'https://api.trongrid.io', testnet: 'https://nile.trongrid.io' },
    explorerTx: { mainnet: 'https://tronscan.org/#/transaction/', testnet: 'https://nile.tronscan.org/#/transaction/' },
    testnetName: 'Nile',
  },
};

export const ECOSYSTEMS: { id: Ecosystem; label: string }[] = [
  { id: 'bitcoin-like', label: 'Bitcoin' },
  { id: 'evm', label: 'Ethereum & EVM networks' },
  { id: 'solana', label: 'Solana' },
  { id: 'tron', label: 'Tron' },
];

/** Robinhood Chain with only its official RPC, so wallet_addEthereumChain never suggests a third-party endpoint. */
const robinhood: ViemChain = { ...robinhoodViem, rpcUrls: { default: { http: [CHAINS.robinhood.rpc.mainnet] } } };
const robinhoodTestnet: ViemChain = { ...robinhoodTestnetViem, rpcUrls: { default: { http: [CHAINS.robinhood.rpc.testnet] } } };

/** viem chain objects keyed by CoinVello chain id. */
const EVM_MAINNET: Partial<Record<Chain, ViemChain>> = {
  ethereum: mainnet,
  base,
  arbitrum,
  optimism,
  polygon,
  bnb: bsc,
  avalanche,
  robinhood,
};
const EVM_TESTNET: Partial<Record<Chain, ViemChain>> = {
  ethereum: sepolia,
  base: baseSepolia,
  arbitrum: arbitrumSepolia,
  optimism: optimismSepolia,
  polygon: polygonAmoy,
  bnb: bscTestnet,
  avalanche: avalancheFuji,
  robinhood: robinhoodTestnet,
};

/**
 * Network of the session on screen. Starts at the build's default (VITE_CV_NETWORK) and follows the session's
 * `livemode` once it loads, so test-mode sessions on the mainnet build use testnet contracts, labels, explorers
 * and wallet networks.
 */
let sessionNetwork: NetworkMode = config.network;

export function setSessionNetwork(livemode: boolean | undefined): void {
  sessionNetwork = livemode === undefined ? config.network : livemode ? 'mainnet' : 'testnet';
}

export function networkMode(): NetworkMode {
  return sessionNetwork;
}

export function viemChainFor(chain: Chain, chainId?: number): ViemChain | undefined {
  // Prefer the chain_id the API sent (authoritative), then fall back to network mode.
  const all = [...Object.values(EVM_MAINNET), ...Object.values(EVM_TESTNET)];
  if (chainId) {
    const hit = all.find((c) => c.id === chainId);
    if (hit) return hit;
  }
  return (sessionNetwork === 'testnet' ? EVM_TESTNET : EVM_MAINNET)[chain];
}

export function evmChainId(chain: Chain, mode: NetworkMode = sessionNetwork): number | undefined {
  return (mode === 'testnet' ? EVM_TESTNET : EVM_MAINNET)[chain]?.id;
}

export function solanaRpc(): string {
  return CHAINS.solana.rpc[sessionNetwork];
}

export function explorerTxUrl(chain: Chain, hash: string): string {
  return CHAINS[chain].explorerTx[sessionNetwork] + hash;
}

export function chainKind(chain: Chain): ChainKind {
  return CHAINS[chain].kind;
}

export function chainLabel(chain: Chain): string {
  const m = CHAINS[chain];
  return sessionNetwork === 'testnet' ? `${m.label} ${m.testnetName}` : m.label;
}

/* ------------------------------------------------------------------------ */
/* Token registry (canonical stablecoin and wrapped-BTC contracts)           */
/* ------------------------------------------------------------------------ */

export type TokenSymbol = 'USDC' | 'USDT' | 'DAI' | 'USDG' | 'EURC' | 'PYUSD' | 'WBTC' | 'cbBTC' | 'BTCB' | 'BTC.b';

/** Known stablecoin contracts. Used to flag API-supplied contracts that don't match (phishing defence). */
export const TOKENS: Record<NetworkMode, Partial<Record<Chain, Partial<Record<TokenSymbol, string>>>>> = {
  mainnet: {
    ethereum: {
      USDC: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',
      USDT: '0xdAC17F958D2ee523a2206206994597C13D831ec7',
      DAI: '0x6B175474E89094C44Da98b954EedeAC495271d0F',
      EURC: '0x1aBaEA1f7C830bD89Acc67eC4af516284b1bC33c',
      PYUSD: '0x6c3ea9036406852006290770BEdFcAbA0e23A0e8',
      WBTC: '0x2260FAC5E5542a773Aa44fBCfeDf7C193bc2C599',
      cbBTC: '0xcbB7C0000aB88B473b1f5aFd9ef808440eed33Bf',
    },
    base: {
      USDC: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913',
      USDT: '0xfde4C96c8593536E31F229EA8f37b2ADa2699bb2',
      DAI: '0x50c5725949A6F0c72E6C4a641F24049A917DB0Cb',
      EURC: '0x60a3E35Cc302bFA44Cb288Bc5a4F316Fdb1adb42',
      WBTC: '0x0555E30da8f98308EdB960aa94C0Db47230d2B9c',
      cbBTC: '0xcbB7C0000aB88B473b1f5aFd9ef808440eed33Bf',
    },
    arbitrum: {
      USDC: '0xaf88d065e77c8cC2239327C5EDb3A432268e5831',
      USDT: '0xFd086bC7CD5C481DCC9C85ebE478A1C0b69FCbb9',
      WBTC: '0x2f2a2543B76A4166549F7aaB2e75Bef0aefC5B0f',
      cbBTC: '0xcbB7C0000aB88B473b1f5aFd9ef808440eed33Bf',
      DAI: '0xDA10009cBd5D07dd0CeCc66161FC93D7c9000da1',
    },
    optimism: {
      USDC: '0x0b2C639c533813f4Aa9D7837CAf62653d097Ff85',
      USDT: '0x94b008aA00579c1307B0EF2c499aD98a8ce58e58',
      WBTC: '0x68f180fcCe6836688e9084f035309E29Bf0A2095',
      DAI: '0xDA10009cBd5D07dd0CeCc66161FC93D7c9000da1',
    },
    polygon: {
      USDC: '0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359',
      USDT: '0xc2132D05D31c914a87C6611C10748AEb04B58e8F',
      WBTC: '0x1BFD67037B42Cf73acF2047067bd4F2C47D9BfD6',
      DAI: '0x8f3Cf7ad23Cd3CaDbD9735AFf958023239c6A063',
    },
    bnb: {
      USDC: '0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d',
      USDT: '0x55d398326f99059fF775485246999027B3197955',
      BTCB: '0x7130d2A12B9BCbFAe4f2634d864A1Ee1Ce3Ead9c',
      DAI: '0x1AF3F329e8BE154074D8769D1FFa4eE058B1DBc3',
    },
    avalanche: {
      USDC: '0xB97EF9Ef8734C71904D8002F8b6Bc66Dd9c48a6E',
      USDT: '0x9702230A8Ea53601f5cD2dc00fDBc13d4dF4A8c7',
      EURC: '0xC891EB4cbdEFf6e073e859e987815Ed1505c2ACD',
      'BTC.b': '0x152b9d0FdC40C096757F570A51E494bd4b943E50',
      DAI: '0xd586E7F844cEa2F87f50152665BCbc2C279D8d70',
    },
    // Paxos Global Dollar (docs.paxos.com USDG mainnet, docs.robinhood.com/chain/contracts).
    robinhood: { USDG: '0x5fc5360D0400a0Fd4f2af552ADD042D716F1d168' },
    solana: {
      USDC: 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v',
      USDT: 'Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB',
      EURC: 'HzwqbKZw8HxMN6bF2yFZNrht3c2iXXzpKcFu7uBEDKtr',
      cbBTC: 'cbbtcf3aa214zXHbiAZQwf4122FBYbraNdFqgw4iMij',
    },
    tron: {
      USDT: 'TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t',
    },
  },
  testnet: {
    // Circle-issued testnet USDC where available; USDT/DAI testnets are unofficial mocks and omitted.
    ethereum: { USDC: '0x1c7D4B196Cb0C7B01d743Fbc6116a902379C7238', EURC: '0x08210F9170F89Ab7658F0B5E3fF39b0E03C594D4', PYUSD: '0xCaC524BcA292aaade2DF8A05cC58F0a65B1B3bB9' },
    base: { USDC: '0x036CbD53842c5426634e7929541eC2318f3dCF7e', EURC: '0x808456652fdb597867f38412077A9182bf77359F' },
    arbitrum: { USDC: '0x75faf114eafb1BDbe2F0316DF893fd58CE46AA4d' },
    optimism: { USDC: '0x5fd84259d66Cd46123540766Be93DFE6D43130D7' },
    polygon: { USDC: '0x41E94Eb019C0762f9Bfcf9Fb1E58725BfB0e7582' },
    avalanche: { USDC: '0x5425890298aed601595a70AB815c96711a31Bc65', EURC: '0x5E44db7996c682E92a960b65AC713a54AD815c6B' },
    bnb: { USDT: '0x337610d27c682E347C9cD60BD4b3b107C9d34dDd' },
    robinhood: { USDG: '0x8F9231B0F448bA9AD045348437458721676c23BC' }, // public faucet MockUSDG
    solana: { USDC: '4zMMC9srt5Ri5X14GAgXhaHii3GnPAEERYPJgZJDncDU', EURC: 'HzwqbKZw8HxMN6bF2yFZNrht3c2iXXzpKcFu7uBEDKtr' },
    tron: { USDT: 'TXYZopYRdj2D9XRtbG411XZZ3kM5VkAeBf' },
  },
};

/**
 * 'known' = contract matches our registry, 'unknown' = asset not in registry
 * (fine for exotic tokens), 'mismatch' = registry has a different canonical
 * contract for this asset/chain -> show a warning, the API may be compromised.
 */
export function checkTokenContract(chain: Chain, asset: string, contract?: string): 'known' | 'unknown' | 'mismatch' {
  if (!contract) return 'known';
  const expected = TOKENS[sessionNetwork][chain]?.[asset as TokenSymbol];
  if (!expected) return 'unknown';
  return expected.toLowerCase() === contract.toLowerCase() ? 'known' : 'mismatch';
}
